/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Slider,
  Button,
  Grid,
  Divider,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import WidgetPreview from "./WidgetPreview";
import { ChatbotConfig, EntityType } from "./types";

interface ConfigurationTabProps {
  entityId: string;
  entityType: EntityType;
  config: ChatbotConfig;
  onSave: (config: ChatbotConfig) => void;
  onChange?: (config: ChatbotConfig) => void;
  loading?: boolean;
}

const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
  config: initialConfig,
  onSave,
  onChange,
  loading = false,
}) => {
  const [config, setConfig] = useState<ChatbotConfig>(initialConfig);
  const { enqueueSnackbar } = useSnackbar();
  // const [updateRepo] = useMutation(UPDATE_DEPLOYMENT);

  useEffect(() => {
    setConfig(initialConfig);
  }, [initialConfig]);

  const updateConfig = (path: string, value: any) => {
    const keys = path.split(".");
    const newConfig = { ...config };
    let current: any = newConfig;

    for (let i = 0; i < keys.length - 1; i += 1) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setConfig(newConfig);
    if (onChange) {
      onChange(newConfig);
    }
  };

  const handleImageUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    path: string,
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validation
      if (!file.type.startsWith("image/")) {
        alert("Please upload an image file.");
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        // 2MB limit
        alert("File size exceeds 2MB limit.");
        return;
      }

      // Create preview URL
      const objectUrl = URL.createObjectURL(file);
      console.log("Created object URL for preview:", objectUrl);

      // Determine the related file field
      const filePathParts = path.split(".");
      const fieldBase = filePathParts[filePathParts.length - 1]; // e.g., avatarUrl
      const fileField =
        fieldBase === "avatarUrl" ? "avatarFile" : "launcherFile";

      console.log(
        `Updating config: ${fieldBase}=${objectUrl}, ${fileField}=[File]`,
      );

      // Perform a single update to avoid race conditions
      const newConfig = { ...config };
      newConfig.appearance = {
        ...newConfig.appearance,
        [fieldBase]: objectUrl,
        [fileField]: file,
      };

      setConfig(newConfig);
      if (onChange) {
        console.log("Calling onChange with new config");
        onChange(newConfig);
      }

      // Reset input value to allow selecting same file again
      event.target.value = "";
    }
  };

  const handleRemoveImage = (path: string) => {
    const filePathParts = path.split(".");
    const fieldBase = filePathParts[filePathParts.length - 1];
    const fileField = fieldBase === "avatarUrl" ? "avatarFile" : "launcherFile";

    const newConfig = { ...config };
    newConfig.appearance = {
      ...newConfig.appearance,
      [fieldBase]: "",
      [fileField]: undefined,
    };

    setConfig(newConfig);
    if (onChange) onChange(newConfig);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Grid container spacing={4}>
        {/* Settings Column */}
        <Grid item xs={12} lg={7}>
          {/* Bot Status */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Bot Status
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => updateConfig("enabled", e.target.checked)}
                  color="success"
                />
              }
              label={config.enabled ? "Bot is Active" : "Bot is Disabled"}
            />
            <Typography variant="caption" color="textSecondary" display="block">
              {config.enabled
                ? "The chatbot is currently live and will respond to customers."
                : "The chatbot is disabled and will not respond to any messages."}
            </Typography>
          </Box>

          {/* Appearance Settings */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Appearance
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                label="Primary Color"
                type="color"
                value={config.appearance.primaryColor}
                onChange={(e) =>
                  updateConfig("appearance.primaryColor", e.target.value)
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Secondary Color"
                type="color"
                value={config.appearance.secondaryColor}
                onChange={(e) =>
                  updateConfig("appearance.secondaryColor", e.target.value)
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Background Color"
                type="color"
                value={config.appearance.backgroundColor}
                onChange={(e) =>
                  updateConfig("appearance.backgroundColor", e.target.value)
                }
                fullWidth
                size="small"
              />
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Border Radius ({config.appearance.borderRadius}px)
                </Typography>
                <Slider
                  value={config.appearance.borderRadius}
                  min={0}
                  max={40}
                  onChange={(_, value) =>
                    updateConfig("appearance.borderRadius", value)
                  }
                  valueLabelDisplay="auto"
                />
              </Box>
            </Box>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Position</InputLabel>
              <Select
                value={config.appearance.position}
                onChange={(e) =>
                  updateConfig("appearance.position", e.target.value)
                }
                label="Position"
                size="small"
              >
                <MenuItem value="bottom-right">Bottom Right</MenuItem>
                <MenuItem value="bottom-left">Bottom Left</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography
                variant="subtitle2"
                color="primary"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                Chat Header Avatar
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                gutterBottom
              >
                This image appears in the top bar of the chat window.
              </Typography>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                {config.appearance.avatarUrl && (
                  <Box
                    sx={{
                      position: "relative",
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "1px solid #ddd",
                    }}
                  >
                    <img
                      src={config.appearance.avatarUrl}
                      alt="Avatar Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                )}

                <Box>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="avatar-upload-button"
                    type="file"
                    onChange={(e) =>
                      handleImageUpload(e, "appearance.avatarUrl")
                    }
                  />
                  <label htmlFor="avatar-upload-button">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      size="small"
                    >
                      Upload Header Avatar
                    </Button>
                  </label>
                </Box>

                {config.appearance.avatarUrl && (
                  <IconButton
                    onClick={() => handleRemoveImage("appearance.avatarUrl")}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <TextField
                label="Header Avatar URL"
                value={config.appearance.avatarUrl || ""}
                onChange={(e) =>
                  updateConfig("appearance.avatarUrl", e.target.value)
                }
                fullWidth
                size="small"
                placeholder="https://example.com/avatar.png"
                sx={{ mb: 3 }}
              />

              <Typography
                variant="subtitle2"
                color="primary"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                Widget Launcher Image
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                gutterBottom
              >
                This image appears on the floating button when the widget is
                collapsed.
              </Typography>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                {config.appearance.launcherImageUrl && (
                  <Box
                    sx={{
                      position: "relative",
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "1px solid #ddd",
                    }}
                  >
                    <img
                      src={config.appearance.launcherImageUrl}
                      alt="Launcher Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                )}

                <Box>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="launcher-upload-button"
                    type="file"
                    onChange={(e) =>
                      handleImageUpload(e, "appearance.launcherImageUrl")
                    }
                  />
                  <label htmlFor="launcher-upload-button">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      size="small"
                    >
                      Upload Launcher Image
                    </Button>
                  </label>
                </Box>

                {config.appearance.launcherImageUrl && (
                  <IconButton
                    onClick={() =>
                      handleRemoveImage("appearance.launcherImageUrl")
                    }
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <TextField
                label="Launcher Image URL"
                value={config.appearance.launcherImageUrl || ""}
                onChange={(e) =>
                  updateConfig("appearance.launcherImageUrl", e.target.value)
                }
                fullWidth
                size="small"
                placeholder="https://example.com/launcher.png"
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={config.appearance.darkMode || false}
                  onChange={(e) =>
                    updateConfig("appearance.darkMode", e.target.checked)
                  }
                />
              }
              label="Dark Mode"
              sx={{ mt: 2 }}
            />
          </Box>

          {/* Behavior Settings */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Behavior
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TextField
              label="Welcome Message"
              value={config.behavior.welcomeMessage}
              onChange={(e) =>
                updateConfig("behavior.welcomeMessage", e.target.value)
              }
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 2 }}
              size="small"
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mb: 2,
              }}
            >
              <Tooltip
                title="Automatically opens the chat widget after a delay when the page loads."
                arrow
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.behavior.autoOpen}
                      onChange={(e) =>
                        updateConfig("behavior.autoOpen", e.target.checked)
                      }
                    />
                  }
                  label="Auto-open Widget"
                />
              </Tooltip>

              <Tooltip
                title="Shows a 'typing...' indicator while the bot is generating a response."
                arrow
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.behavior.typingAnimation}
                      onChange={(e) =>
                        updateConfig(
                          "behavior.typingAnimation",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label="Typing Animation"
                />
              </Tooltip>

              <Tooltip
                title="Plays a notification sound when a new message is received."
                arrow
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.behavior.soundEnabled || false}
                      onChange={(e) =>
                        updateConfig("behavior.soundEnabled", e.target.checked)
                      }
                    />
                  }
                  label="Sound"
                />
              </Tooltip>

              <Tooltip
                title="Allows the bot to escalate the conversation to a human agent when needed."
                arrow
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.behavior.handoffEnabled ?? true}
                      onChange={(e) =>
                        updateConfig(
                          "behavior.handoffEnabled",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label="Handoff"
                />
              </Tooltip>
            </Box>

            {config.behavior.autoOpen && (
              <TextField
                label="Auto-open Delay (ms)"
                type="number"
                value={config.behavior.autoOpenDelay}
                onChange={(e) =>
                  updateConfig(
                    "behavior.autoOpenDelay",
                    parseInt(e.target.value, 10),
                  )
                }
                fullWidth
                sx={{ mb: 2 }}
                size="small"
              />
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                label="Bot Header Text"
                value={config.behavior.botTakeoverName}
                onChange={(e) =>
                  updateConfig("behavior.botTakeoverName", e.target.value)
                }
                fullWidth
                size="small"
                inputProps={{ maxLength: 50 }}
                InputLabelProps={{ shrink: !!config.behavior.botTakeoverName }}
                helperText={`${
                  config.behavior.botTakeoverName?.length || 0
                }/50`}
              />
              <TextField
                label="Agent Header Text"
                value={config.behavior.agentTakeoverName}
                onChange={(e) =>
                  updateConfig("behavior.agentTakeoverName", e.target.value)
                }
                fullWidth
                size="small"
                inputProps={{ maxLength: 50 }}
                InputLabelProps={{
                  shrink: !!config.behavior.agentTakeoverName,
                }}
                helperText={`${
                  config.behavior.agentTakeoverName?.length || 0
                }/50`}
              />
            </Box>
          </Box>

          {/* Home Screen Links */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="h6">Home Screen Links</Typography>
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="outlined"
                onClick={() => {
                  const currentLinks = config.links || [];
                  const newLinks = [...currentLinks];
                  newLinks.push({
                    text: "New Link",
                    url: "https://",
                    iconUrl: "",
                  });
                  updateConfig("links", newLinks);
                }}
              >
                Add Link
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mb: 2, display: "block" }}
            >
              These links appear on the chatbot home screen. You can add up to 6
              links.
            </Typography>

            {(config.links || []).map((link, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  position: "relative",
                  bgcolor: "rgba(0,0,0,0.01)",
                }}
              >
                <IconButton
                  size="small"
                  color="error"
                  sx={{ position: "absolute", top: 12, right: 8 }}
                  onClick={() => {
                    const currentLinks = config.links || [];
                    const newLinks = currentLinks.filter((_, i) => i !== index);
                    updateConfig("links", newLinks);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>

                <Grid container spacing={2} sx={{ pr: 5 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Link Text"
                      value={link.text}
                      onChange={(e) => {
                        const currentLinks = config.links || [];
                        const newLinks = [...currentLinks];
                        newLinks[index] = {
                          ...newLinks[index],
                          text: e.target.value,
                        };
                        updateConfig("links", newLinks);
                      }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Link URL"
                      value={link.url}
                      onChange={(e) => {
                        const currentLinks = config.links || [];
                        const newLinks = [...currentLinks];
                        newLinks[index] = {
                          ...newLinks[index],
                          url: e.target.value,
                        };
                        updateConfig("links", newLinks);
                      }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Icon URL (Optional)"
                      value={link.iconUrl || ""}
                      onChange={(e) => {
                        const currentLinks = config.links || [];
                        const newLinks = [...currentLinks];
                        newLinks[index] = {
                          ...newLinks[index],
                          iconUrl: e.target.value,
                        };
                        updateConfig("links", newLinks);
                      }}
                      fullWidth
                      size="small"
                      placeholder="https://example.com/icon.png"
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={async () => {
              try {
                console.log("Saving chatbot config:", config);
                await onSave(config);
              } catch (e) {
                enqueueSnackbar("Error saving configuration", {
                  variant: "error",
                });
              }
            }}
            fullWidth
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {loading ? "Saving..." : "Save Configuration"}
          </Button>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Box
            sx={{
              position: { xs: "static", lg: "sticky" },
              top: 20,
              mt: { xs: 4, lg: 0 },
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <WidgetPreview config={config} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConfigurationTab;
